// JobsPage — back-office job management.
//
// Visual language: Satori-style table + filter chips + side-panel detail.
// The detail panel is the workhorse here — JobDetailPanel (extracted)
// shows the customer, the quote, the line items, and the status
// transition dropdown. Row click opens the full /jobs/:id page; Eye icon
// opens the side panel.

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Eye, Home } from 'lucide-react';
import { useSortable } from '../hooks/useSortable.js';
import { SortHeader } from '../components/SortHeader.js';
import { getJobs, getJobDetail } from '../api/client.js';
import { isBlockingError } from '../lib/isBlockingError.js';
import { poSortValue } from '../lib/po-sort.js';
import type { JobListRow, JobStatus, ProductLine } from '@gutterinv/shared';
import { Card } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { formatCents, formatDate } from '../lib/format.js';
import { STATUS_LABEL } from '../lib/labels.js';
import { JobStatusBadge } from '../components/JobStatusBadge.js';
import { JobDetailPanel } from '../components/JobDetailPanel.js';
import { CreateJobDialog } from '../components/CreateJobDialog.js';
import { TemplatesSection } from '../components/TemplatesSection.js';
import { qk } from '../lib/queryKeys.js';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from '../components/ui/sheet.js';
import { cn } from '../lib/utils.js';

// Synthetic filter values: 'active' is everything except completed/disqualified.
type JobsFilter = 'all' | 'active' | JobStatus;

// Quick filters: synthetic "everything except completed/disqualified" + "everything".
const QUICK_FILTERS: ReadonlyArray<{ value: 'active' | 'all'; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
];

// Stage filters: actual JobStatus values, surfaced via the "Job Stage" dropdown.
const STAGE_FILTERS: ReadonlyArray<{ value: JobStatus; label: string }> = [
  { value: 'created', label: 'Created' },
  { value: 'estimated', label: 'Estimated' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'installed', label: 'Installed' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'completed', label: 'Completed' },
  { value: 'disqualified', label: 'Disqualified' },
];

function isActiveStatus(status: JobStatus): boolean {
  return status !== 'completed' && status !== 'disqualified';
}

export function JobsPage() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobsFilter>('active');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Product line tab — All / Gutter / Insulation. Synced with the URL
  // hash so a refresh / back / share preserves the active tab
  // (per feedback_refresh_preserves_view).
  const [productLineFilter, setProductLineFilter] = useState<'all' | ProductLine>(() => {
    if (typeof window === 'undefined') return 'all';
    const m = window.location.hash.match(/productLine=(gutter|insulation|all)/);
    const v = m?.[1];
    return v === 'gutter' || v === 'insulation' ? v : 'all';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = productLineFilter === 'all' ? '' : `productLine=${productLineFilter}`;
    if ((window.location.hash.replace(/^#/, '') || '') !== next) {
      window.history.replaceState(null, '', next ? `#${next}` : window.location.pathname);
    }
  }, [productLineFilter]);

  const { data: jobs, isLoading, error } = useQuery({
    queryKey: qk.jobs.all(),
    queryFn: getJobs,
  });

  const filtered = useMemo(() => {
    if (!jobs) return [];
    const q = searchText.trim().toLowerCase();
    return jobs.filter((j) => {
      if (productLineFilter !== 'all' && j.productLine !== productLineFilter) {
        return false;
      }
      if (statusFilter === 'active') {
        if (!isActiveStatus(j.status)) return false;
      } else if (statusFilter !== 'all' && j.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      return (
        j.displayName.toLowerCase().includes(q) ||
        j.address.toLowerCase().includes(q) ||
        j.customerName.toLowerCase().includes(q) ||
        (j.poNumber?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [jobs, searchText, statusFilter, productLineFilter]);

  // Counts for the All / Gutter / Insulation tab badges (full set,
  // before status/search filters apply, since the tab itself is the
  // outermost cut).
  const productLineCounts = useMemo(() => {
    const c = { all: 0, gutter: 0, insulation: 0 };
    if (!jobs) return c;
    for (const j of jobs) {
      c.all++;
      if (j.productLine === 'gutter') c.gutter++;
      else if (j.productLine === 'insulation') c.insulation++;
    }
    return c;
  }, [jobs]);

  type JobSortKey =
    | 'job'
    | 'po'
    | 'address'
    | 'type'
    | 'status'
    | 'quote'
    | 'installDate'
    | 'created';
  const jobAccessor = useCallback(
    (j: JobListRow, k: JobSortKey): string | number | null => {
      switch (k) {
        case 'job':
          return j.displayName;
        case 'po':
          // Numeric sort, not lexicographic — see lib/po-sort.ts.
          return poSortValue(j.poNumber);
        case 'address':
          return j.address;
        case 'type':
          return j.productLine;
        case 'status':
          return j.status;
        case 'quote':
          return j.quoteAmountCents;
        case 'installDate':
          return j.scheduledStartDate;
        case 'created':
          return j.createdAt;
      }
    },
    [],
  );
  const { sorted, sort, toggle } = useSortable(filtered, jobAccessor);

  const statusCounts = useMemo(() => {
    const counts: Record<JobStatus, number> = {
      created: 0,
      estimated: 0,
      quoted: 0,
      accepted: 0,
      scheduled: 0,
      installed: 0,
      invoiced: 0,
      completed: 0,
      disqualified: 0,
    };
    if (!jobs) return counts;
    for (const j of jobs) counts[j.status]++;
    return counts;
  }, [jobs]);

  if (isLoading) {
    return <JobsSkeleton />;
  }

  if (isBlockingError(error, !!jobs)) {
    return (
      <Card className="border-destructive p-6">
        <div className="font-semibold text-destructive">Failed to load jobs</div>
        <div className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  return (
    <div className="pt-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">
            {filtered.length} of {jobs?.length ?? 0} jobs
            {statusFilter !== 'all' &&
              ` · ${
                statusFilter === 'active'
                  ? 'Active'
                  : STATUS_LABEL[statusFilter]
              }`}
            {searchText && ` · matching "${searchText}"`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)}>
            <Home className="h-4 w-4" />
            Manage templates
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            New job
          </Button>
        </div>
      </div>

      {/* Product line tabs — All / Gutter / Insulation. Outermost
          filter; the status pills + search box below operate on
          whatever tab is active. URL hash persists the choice across
          refresh / back / share. */}
      <div className="flex gap-1">
        {(
          [
            { value: 'all' as const, label: 'All', count: productLineCounts.all },
            { value: 'gutter' as const, label: 'Gutter', count: productLineCounts.gutter },
            {
              value: 'insulation' as const,
              label: 'Insulation',
              count: productLineCounts.insulation,
            },
          ]
        ).map((tab) => {
          const active = productLineFilter === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setProductLineFilter(tab.value)}
              className={cn(
                'rounded-md px-4 py-2 text-sm transition-all duration-150',
                // Pop-out on hover (lift + navy shadow), press back down on click.
                'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                active
                  ? 'bg-sidebar font-bold text-white'
                  : 'font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground',
              )}
            >
              {tab.label}
              <span
                className={cn(
                  'ml-1.5 rounded-sm px-1 text-[0.6875rem]',
                  active ? 'bg-white/20 text-white' : 'bg-secondary text-taupe',
                )}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by name, address, customer, PO..."
            className="h-9 pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {QUICK_FILTERS.map((f) => {
            const active = statusFilter === f.value;
            const count =
              f.value === 'all'
                ? jobs?.length ?? 0
                : (jobs ?? []).filter((j) => isActiveStatus(j.status)).length;
            return (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={cn(
                  'h-8 rounded-md border px-3 text-[0.8125rem] font-medium transition-all duration-150',
                  // Pop-out on hover (lift + navy shadow), press back down on click.
                  'transform-gpu hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-2px_rgba(27,42,63,0.18)] active:translate-y-0 active:shadow-sm',
                  active
                    ? 'border-primary bg-accent text-primary'
                    : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                )}
              >
                {f.label}
                <span
                  className={cn(
                    'ml-1.5 rounded-sm px-1 text-[0.6875rem]',
                    active ? 'bg-primary/10 text-primary' : 'bg-secondary text-taupe',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
          <span className="ml-1 text-[0.8125rem] font-medium text-muted-foreground">
            Job Stage
          </span>
          <select
            value={
              statusFilter !== 'all' && statusFilter !== 'active' ? statusFilter : ''
            }
            onChange={(e) => {
              const v = e.target.value;
              setStatusFilter(v === '' ? 'all' : (v as JobStatus));
            }}
            className={cn(
              'h-8 rounded-md border px-2 text-[0.8125rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              statusFilter !== 'all' && statusFilter !== 'active'
                ? 'border-primary bg-accent text-primary'
                : 'border-input bg-card text-foreground',
            )}
          >
            <option value="" className="bg-card text-foreground">
              All stages
            </option>
            {STAGE_FILTERS.map((f) => (
              <option
                key={f.value}
                value={f.value}
                className="bg-card text-foreground"
              >
                {f.label} ({statusCounts[f.value]})
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card className="overflow-auto p-0" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-secondary/95 backdrop-blur-sm">
            <tr className="border-b border-border">
              <SortHeader label="Job" active={sort.key === 'job'} dir={sort.dir} onClick={() => toggle('job')} />
              {productLineFilter === 'all' && (
                <SortHeader label="Type" active={sort.key === 'type'} dir={sort.dir} onClick={() => toggle('type')} />
              )}
              <SortHeader label="PO #" active={sort.key === 'po'} dir={sort.dir} onClick={() => toggle('po')} />
              <SortHeader label="Address" active={sort.key === 'address'} dir={sort.dir} onClick={() => toggle('address')} />
              <SortHeader label="Status" active={sort.key === 'status'} dir={sort.dir} onClick={() => toggle('status')} className="w-px whitespace-nowrap" />
              <SortHeader label="Quote" active={sort.key === 'quote'} dir={sort.dir} onClick={() => toggle('quote')} align="right" className="w-px whitespace-nowrap" />
              <SortHeader label="Created" active={sort.key === 'created'} dir={sort.dir} onClick={() => toggle('created')} align="right" className="w-px whitespace-nowrap" />
              <SortHeader label="Install date" active={sort.key === 'installDate'} dir={sort.dir} onClick={() => toggle('installDate')} align="right" className="w-px whitespace-nowrap" />
              {/* Trailing action column for the per-row Quick view (eyeball). Compact,
                  fixed-width, with its own right gutter so it isn't jammed against the edge. */}
              <th className="w-px py-3 pl-3 pr-5">
                <span className="sr-only">Quick view</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={productLineFilter === 'all' ? 9 : 8} className="px-4 py-12 text-center">
                  <div className="text-sm font-medium text-muted-foreground">
                    No jobs match
                  </div>
                  <div className="text-xs text-taupe">
                    Try clearing the search or filter
                  </div>
                </td>
              </tr>
            ) : (
              sorted.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  showType={productLineFilter === 'all'}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  onQuickView={() => setSelectedId(job.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Sheet
        open={selectedId !== null}
        onOpenChange={(o) => !o && setSelectedId(null)}
      >
        <SheetContent>
          {selectedId && (
            <JobDetailPanel
              jobId={selectedId}
              onDeleted={() => setSelectedId(null)}
              onClose={() => setSelectedId(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Manage templates</SheetTitle>
            <SheetDescription>
              Named price presets for the New Job dialog.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <TemplatesSection />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <CreateJobDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function JobRow({
  job,
  showType,
  onClick,
  onQuickView,
}: {
  job: JobListRow;
  /** Only shown on the All tab — hidden when filtered to a single product line. */
  showType: boolean;
  /** Row click — navigates to the full 6-card detail page. */
  onClick: () => void;
  /** Quick-view icon click — opens the side panel without leaving the list. */
  onQuickView: () => void;
}) {
  const queryClient = useQueryClient();
  const prefetchJob = () => {
    void queryClient.prefetchQuery({
      queryKey: ['job', job.id],
      queryFn: () => getJobDetail(job.id),
      staleTime: 10_000,
    });
  };
  return (
    <tr
      onClick={onClick}
      onMouseEnter={prefetchJob}
      className="group cursor-pointer border-b border-border last:border-b-0 transition-colors hover:bg-primary/[0.08]"
    >
      <td className="px-4 py-3">
        <div className="text-[0.9375rem] font-semibold text-foreground">
          {job.displayName}
        </div>
        <div className="text-xs text-taupe">
          {job.customerName}
          <span className="mx-1.5">·</span>
          {job.customerType}
        </div>
      </td>
      {showType && (
        <td className="px-4 py-3">
          <span className="text-[0.8125rem] capitalize text-muted-foreground">
            {job.productLine}
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        {job.poNumber ? (
          <span className="font-mono text-sm text-foreground">{job.poNumber}</span>
        ) : (
          <span className="text-taupe">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="max-w-[18rem] truncate text-[0.8125rem] text-muted-foreground">
          {job.address}
        </div>
      </td>
      <td className="w-px whitespace-nowrap px-4 py-3">
        <JobStatusBadge status={job.status} />
      </td>
      <td className="w-px whitespace-nowrap px-4 py-3 text-right">
        {job.quoteAmountCents !== null ? (
          <span className="tnum text-sm font-semibold text-foreground">
            {formatCents(job.quoteTotalCents)}
          </span>
        ) : (
          <span className="text-taupe">—</span>
        )}
      </td>
      <td className="w-px whitespace-nowrap px-4 py-3 text-right">
        <span className="text-xs text-taupe">{formatDate(job.createdAt)}</span>
      </td>
      <td className="w-px whitespace-nowrap px-4 py-3 text-right">
        {job.scheduledStartDate ? (
          <span className="text-xs text-muted-foreground">
            {formatDate(job.scheduledStartDate)}
          </span>
        ) : (
          <span className="text-taupe">—</span>
        )}
      </td>
      {/* Dedicated trailing action column — compact, with a left gap after Install
          date and its own right gutter (pr-5) so the eyeball isn't jammed at the edge. */}
      <td className="w-px whitespace-nowrap py-3 pl-3 pr-5 text-right">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onQuickView();
          }}
          className="rounded p-1 text-taupe opacity-0 transition-opacity hover:bg-accent/60 hover:text-foreground group-hover:opacity-100"
          title="Quick view"
        >
          <Eye className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function JobsSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-9 w-48" />
      <Skeleton className="h-9 w-full max-w-md" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}
